import React from 'react';
// ════════════════════════════════════════════════════════════════════════════
// tiposAcesso.js — r93
// ────────────────────────────────────────────────────────────────────────────
// Define os 9 tipos de acesso do módulo de Acessos e quais atributos cada
// tipo possui (matriz "Acessos × Tipo" da especificação PDF).
//
// Pra adicionar um tipo novo: insira aqui + restart frontend.
// ════════════════════════════════════════════════════════════════════════════

export const TIPOS_ACESSO = [
  {
    codigo:    'vpn',
    label:     'VPN',
    // r97: adicionada 'porta' logo após 'ip' a pedido do usuário
    atributos: ['aplicativo', 'download', 'ip', 'porta', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'terminal-server',
    label:     'Terminal Server',
    atributos: ['ip_url', 'porta', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'banco-dados',
    label:     'Banco de Dados',
    atributos: ['ip_url', 'porta', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'protheus',
    label:     'Protheus',
    atributos: ['webapp', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'license-server',
    label:     'License Server',
    atributos: ['ip_url', 'porta', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'dbaccess',
    label:     'DbAccess',
    atributos: ['ip_url', 'porta', 'usuario', 'senha', 'outros'],
  },
  {
    codigo:    'portal-totvs',
    label:     'Portal Totvs',
    atributos: ['usuario', 'senha', 'outros'],
  },
  {
    codigo:    'portal-cliente',
    label:     'Portal Cliente',
    atributos: ['ip_url', 'porta', 'usuario', 'senha', 'outros'],   // r245: +outros
  },
  {
    codigo:    'app-chamados',
    label:     'App Chamados',
    atributos: ['ip_url', 'usuario', 'senha', 'outros'],   // r245: +outros
  },
];

// r245: visibilidade do acesso. 'R' = só quem restringiu + admins (o backend
// filtra); 'C' = todos com permissão da rotina. Padrão 'C'.
export const VISIBILIDADES = {
  C: { label: 'Compartilhado', cor: '#1D9E75', bg: '#f0fff4' },
  R: { label: 'Restrito',      cor: '#e53935', bg: '#fff0f0' },
};
export const VISIBILIDADE_PADRAO = 'C';

// Labels dos atributos pra exibir na UI
export const LABELS_ATRIBUTO = {
  'aplicativo': 'Aplicativo',
  'download':   'Download',
  'ip':         'IP',
  'ip_url':     'IP/URL',
  'porta':      'Porta',
  'usuario':    'Usuário',
  'senha':      'Senha',
  'outros':     'Outros',
  'webapp':     'WebApp',
};

// Ajudante: dado um código, retorna a definição completa do tipo
export function getTipoAcesso(codigo) {
  return TIPOS_ACESSO.find(t => t.codigo === codigo);
}

// Ajudante: lista de atributos de um tipo (ordem da spec)
export function atributosDoTipo(codigo) {
  return getTipoAcesso(codigo)?.atributos || [];
}

// Ajudante: detecta se atributo é "senha" (renderização especial — esconder/mostrar)
export function ehSenha(tipoAttr) {
  return tipoAttr === 'senha';
}

// ════════════════════════════════════════════════════════════════════════════
// r98: ícones SVG por tipo de acesso
// ────────────────────────────────────────────────────────────────────────────
// Cada tipo retorna um SVG no padrão Lucide-like (stroke, 16x16, currentColor).
// Use no JSX assim:
//   {iconeDoTipo('vpn', { width: 16, height: 16, color: '#003399' })}
// ════════════════════════════════════════════════════════════════════════════

const ICONES = {
  // VPN — escudo (proteção/túnel seguro)
  'vpn': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 L4 5 v6 c0 5 3.5 9 8 11 c4.5 -2 8 -6 8 -11 V5 z"/>
      <path d="M9 12 l2 2 l4 -4"/>
    </svg>
  ),
  // Terminal Server — monitor com prompt
  'terminal-server': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21 h8"/>
      <path d="M12 17 v4"/>
      <path d="M6 8 l3 2 l-3 2"/>
      <path d="M11 12 h4"/>
    </svg>
  ),
  // Banco de Dados — cilindros empilhados (ícone universal)
  'banco-dados': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M3 5 v6 c0 1.66 4 3 9 3 s9 -1.34 9 -3 V5"/>
      <path d="M3 11 v6 c0 1.66 4 3 9 3 s9 -1.34 9 -3 v-6"/>
    </svg>
  ),
  // Protheus — engrenagem (sistema ERP)
  'protheus': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15 a1.65 1.65 0 0 0 0.33 1.82 l0.06 0.06 a2 2 0 1 1 -2.83 2.83 l-0.06 -0.06 a1.65 1.65 0 0 0 -1.82 -0.33 a1.65 1.65 0 0 0 -1 1.51 V21 a2 2 0 0 1 -4 0 v-0.09 A1.65 1.65 0 0 0 9 19.4 a1.65 1.65 0 0 0 -1.82 0.33 l-0.06 0.06 a2 2 0 1 1 -2.83 -2.83 l0.06 -0.06 a1.65 1.65 0 0 0 0.33 -1.82 a1.65 1.65 0 0 0 -1.51 -1 H3 a2 2 0 0 1 0 -4 h0.09 A1.65 1.65 0 0 0 4.6 9 a1.65 1.65 0 0 0 -0.33 -1.82 l-0.06 -0.06 a2 2 0 1 1 2.83 -2.83 l0.06 0.06 a1.65 1.65 0 0 0 1.82 0.33 H9 a1.65 1.65 0 0 0 1 -1.51 V3 a2 2 0 0 1 4 0 v0.09 a1.65 1.65 0 0 0 1 1.51 a1.65 1.65 0 0 0 1.82 -0.33 l0.06 -0.06 a2 2 0 1 1 2.83 2.83 l-0.06 0.06 a1.65 1.65 0 0 0 -0.33 1.82 V9 a1.65 1.65 0 0 0 1.51 1 H21 a2 2 0 0 1 0 4 h-0.09 a1.65 1.65 0 0 0 -1.51 1 z"/>
    </svg>
  ),
  // License Server — chave (licença)
  'license-server': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="3.5"/>
      <path d="M10 13 l9 -9"/>
      <path d="M16 5 l3 3"/>
      <path d="M14 7 l3 3"/>
    </svg>
  ),
  // DbAccess — cilindro + setinhas (conexão de banco)
  'dbaccess': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="6" cy="6" rx="4" ry="2"/>
      <path d="M2 6 v6 c0 1.1 1.8 2 4 2 s4 -0.9 4 -2 V6"/>
      <path d="M14 8 l3 3 l-3 3"/>
      <path d="M10 11 h7"/>
      <rect x="17" y="14" width="5" height="7" rx="1"/>
      <path d="M19.5 14 v-2"/>
    </svg>
  ),
  // Portal Totvs — globo (portal web)
  'portal-totvs': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M2 12 h20"/>
      <path d="M12 2 a15 15 0 0 1 0 20"/>
      <path d="M12 2 a15 15 0 0 0 0 20"/>
    </svg>
  ),
  // Portal Cliente — pessoa + janela
  'portal-cliente': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2"/>
      <path d="M3 8 h18"/>
      <circle cx="12" cy="13" r="2"/>
      <path d="M8 18 c0 -2 2 -3 4 -3 s4 1 4 3"/>
    </svg>
  ),
  // App Chamados — ticket / suporte
  'app-chamados': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9 a3 3 0 0 1 0 6 v3 a1 1 0 0 0 1 1 h18 a1 1 0 0 0 1 -1 v-3 a3 3 0 0 1 0 -6 V6 a1 1 0 0 0 -1 -1 H3 a1 1 0 0 0 -1 1 z"/>
      <path d="M13 5 v2"/>
      <path d="M13 11 v2"/>
      <path d="M13 17 v2"/>
    </svg>
  ),
};

// Fallback genérico (chave) — caso o tipo seja desconhecido
const ICONE_GENERICO = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="3.5"/>
    <path d="M10 13 l9 -9"/>
    <path d="M16 5 l3 3"/>
  </svg>
);

export function iconeDoTipo(codigo, { width = 16, height = 16, color = 'currentColor' } = {}) {
  const svg = ICONES[codigo] || ICONE_GENERICO;
  return React.cloneElement(svg, {
    width, height, style: { color, flexShrink: 0 },
  });
}
